'use client';

import {
    JackpotMultiplierPopup,
    type JackpotMultiplierPopupParams,
    type ParsedJackpotMultiplierPopupParams
} from '@fanduel/casino-swj-consumer';
import { useStore } from '@/store/store';
import { Fragment, Suspense, useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { getConfig } from '@/utils/config/config';
import { getCookieValue } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies/constants';
import { optInManagerService } from '@/app/components/site-wide-jackpot/hooks/use-opt-in-manager';
import { ddRumError } from '@/utils/datadog';
import { ddErrorSwjOptin } from '@/utils/datadog/site-wide-jackpot';
import { DD_USE_CASES } from '@/utils/datadog/types';
import { POPUPS } from '@/app/components/popup/constants';
import type { SupportLink } from '@/types/layout/link-view-model';
import { useGlobalContext } from '@/store/contexts/global-context';
import {
    getSwjModalOpenedProps,
    getSwjModalClosedProps,
    getSwjOptInOnProps,
    getSwjOptInOffProps
} from '@/tracking/amplitude/events/general/swj-opt-in';
import { useAccountUm } from '@/hooks/use-account-um';

type SWJOptInPopUpProps = {
    params: Record<string, unknown>;
};

export const SWJOptInPopUp: React.FC<SWJOptInPopUpProps> = ({ params }) => {
    const { closePopup } = useStore((s) => s.popup);
    const { multiplier, updatedTiers, setMultiplier } = useStore((s) => s.siteWideJackpot);
    const { sendEvent } = useStore((s) => s.tracking);
    const [isContainerReady, setIsContainerReady] = useState(false);
    const swjParams = params?.swjParams;
    const modalModule = params?.module as string | undefined;
    const jackpotId = (swjParams as { jackpotId?: string })?.jackpotId;
    const support = (swjParams as { support?: SupportLink })?.support;
    const { queuePopup } = useStore((s) => s.popup);
    const config = getConfig();
    const region = getCookieValue(COOKIES.REGION) || 'mi';
    const gthBaseUrl = config?.apis?.SiteWideJackpotClient?.host?.replace('statePlaceholder', region.toLowerCase());
    const { isMobile, isTablet } = useGlobalContext();
    const handleError = useCallback(
        (error: { reason: string; message?: string | undefined }, errorMultiplier?: number) => {
            const errorMessage = error?.reason || error?.message || '';

            ddRumError(ddErrorSwjOptin(errorMessage, DD_USE_CASES.SITE_WIDE_JACKPOT, {}));

            queuePopup({
                type: POPUPS.SWJ_ERROR,
                params: {
                    errorCode: errorMessage,
                    support,
                    multiplier: errorMultiplier
                }
            });
        },
        [queuePopup, support]
    );
    const { getSession } = useAccountUm();

    const displayOrientation = useMemo(() => {
        // Check needed due to window.matchMedia being available on desktop too
        if (!isMobile && !isTablet) {
            return 'desktop';
        }

        // Only check orientation for mobile/tablet devices
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
        }

        // Fallback for mobile/tablet when matchMedia is not available
        return 'portrait';
    }, [isMobile, isTablet]);

    useEffect(() => {
        setIsContainerReady(true);
    }, []);

    // Configure the OptInManager with endpoint, origin, and jackpotId from params
    useEffect(() => {
        if (jackpotId && gthBaseUrl) {
            optInManagerService.configure(`https://${gthBaseUrl}`, 'lobby', jackpotId, {
                errorCallback: handleError,
                callback: (v) => {
                    if (typeof v === 'number') {
                        setMultiplier(v as 0 | 1 | 2);
                    }
                }
            });
        }
    }, [jackpotId, gthBaseUrl, handleError, setMultiplier]);

    const hasLoggedOpen = useRef(false);
    useEffect(() => {
        if (swjParams && !hasLoggedOpen.current) {
            hasLoggedOpen.current = true;
            sendEvent([
                {
                    destination: 'amplitude',
                    eventProps: getSwjModalOpenedProps(multiplier, updatedTiers, modalModule)
                }
            ]);
        }
        // multiplier and updatedTiers intentionally omitted — capture state at modal open, not on every change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [swjParams, sendEvent, modalModule]);

    const handleClose = useCallback(() => {
        sendEvent([
            {
                destination: 'amplitude',
                eventProps: getSwjModalClosedProps(multiplier, updatedTiers, modalModule)
            }
        ]);
        closePopup();
    }, [sendEvent, multiplier, updatedTiers, modalModule, closePopup]);

    const logActions = useMemo(
        () => ({
            logOptInOutToggle: (_optedInOption: string, _tiers: unknown, newMultiplier?: number) => {
                const eventMultiplier = newMultiplier ?? 0;
                const props =
                    eventMultiplier > 0
                        ? getSwjOptInOnProps({ multiplier: eventMultiplier, updatedTiers })
                        : getSwjOptInOffProps({ previousMultiplier: multiplier, updatedTiers });
                sendEvent([{ destination: 'amplitude', eventProps: props }]);
            }
        }),
        [sendEvent, updatedTiers, multiplier]
    );

    if (!swjParams) {
        return null;
    }

    return (
        <div
            data-t-module="fdc_jackpots"
            data-t-multiplier={multiplier}
            data-t-optinstatus={multiplier > 0 ? 'opted_in' : 'opted_out'}>
            <Fragment>
                <div id="swj-popup-container"></div>
                {isContainerReady && (
                    <Suspense>
                        <JackpotMultiplierPopup
                            origin={'lobby'}
                            optedIn={multiplier}
                            containerToAttach="swj-popup-container"
                            primaryButtonText="Play games"
                            handleClose={handleClose}
                            params={swjParams as JackpotMultiplierPopupParams | ParsedJackpotMultiplierPopupParams}
                            deviceType={displayOrientation}
                            tiers={{ updatedTiers }}
                            brand={process.env.BRAND}
                            theme={process.env.PRODUCT}
                            logActions={logActions}
                            getSession={getSession}
                        />
                    </Suspense>
                )}
            </Fragment>
        </div>
    );
};
